import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { permissionService } from '../services/role.service';
import { authService } from '../services/auth.service';

interface PermissionContextValue {
  permissions: Set<string>;
  loading: boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (...permissions: string[]) => boolean;
  hasAllPermissions: (...permissions: string[]) => boolean;
  refresh: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextValue>({
  permissions: new Set(),
  loading: true,
  hasPermission: () => false,
  hasAnyPermission: () => false,
  hasAllPermissions: () => false,
  refresh: async () => {},
});

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const lastFocusFetchRef = useRef(0);

  // background=true: silently refresh without toggling loading state (prevents UI flicker)
  const fetchPermissions = useCallback(async (background = false) => {
    if (!authService.isAuthenticated()) {
      setPermissions(new Set());
      setLoading(false);
      return;
    }
    if (!background) setLoading(true);
    try {
      const perms = await permissionService.getMyPermissions();
      setPermissions(new Set(perms));
    } catch {
      if (!background) setPermissions(new Set());
    } finally {
      if (!background) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Re-fetch after login (Login component dispatches 'auth-changed') and after a
  // Company switch — AppRoles are per-company, so the same account can hold a
  // different permission set in each company it is mapped to.
  useEffect(() => {
    const onAuthChanged = () => fetchPermissions();
    window.addEventListener('auth-changed', onAuthChanged);
    window.addEventListener('company-switched', onAuthChanged);
    // Grants are per login mode (Actual / Compliance are isolated sets): re-fetch on a mode switch.
    window.addEventListener('mode-switched', onAuthChanged);
    return () => {
      window.removeEventListener('auth-changed', onAuthChanged);
      window.removeEventListener('company-switched', onAuthChanged);
      window.removeEventListener('mode-switched', onAuthChanged);
    };
  }, [fetchPermissions]);

  // Silently re-fetch on window focus to pick up admin permission changes.
  // Background mode keeps existing permissions visible during refresh (no loading flash).
  // 30-second cooldown prevents hammering the API on rapid Cmd+Tab switches.
  useEffect(() => {
    const onFocus = () => {
      if (!authService.isAuthenticated()) return;
      const now = Date.now();
      if (now - lastFocusFetchRef.current < 30_000) return;
      lastFocusFetchRef.current = now;
      fetchPermissions(true);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchPermissions]);

  // Fail-closed: return false until permissions are loaded so privileged
  // sidebar/UI items don't briefly appear to under-privileged users.
  const value: PermissionContextValue = {
    permissions,
    loading,
    hasPermission: (p: string) => !loading && permissions.has(p),
    hasAnyPermission: (...ps: string[]) => !loading && ps.some(p => permissions.has(p)),
    hasAllPermissions: (...ps: string[]) => !loading && ps.every(p => permissions.has(p)),
    refresh: fetchPermissions,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => useContext(PermissionContext);
